import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import NotificationStatus from '../../types/notification';

export interface INotificationMessageCardProps {
  message: {
    status: string;
    title: string;
    message: string;
    time: string;
    profilePic?: string;
  };
  index: number;
  totalItems: number;
  onClick?: () => void;
}
const NotificationMessageCard = ({
  message,
  index,
  totalItems,
  onClick,
}: INotificationMessageCardProps) => {
  const { t, i18n } = useTranslation();
  const appLanguage = i18n.language;
  const renderIcon = () => {
    switch (message?.status) {
      case NotificationStatus.SUCCESS:
        return (
          <img
            src="/assets/icons/success-notification.svg"
            alt="Success"
            style={{ width: '24px', height: '24px' }}
          />
        );
      case NotificationStatus.WARNING:
        return (
          <img
            src="/assets/icons/warning-notification.svg"
            alt="Warning"
            style={{ width: '24px', height: '24px' }}
          />
        );
      case NotificationStatus.DANGER:
        return (
          <img
            src="/assets/icons/danger-notification.svg"
            alt="Danger"
            style={{ width: '24px', height: '24px' }}
          />
        );
      case NotificationStatus.CLIENT_NOTIFICATION:
        return message.profilePic ? (
          <img
            src={message.profilePic}
            alt="Client Notification"
            style={{ width: '24px', height: '24px' }}
          />
        ) : (
          <img
            src="/assets/icons/client.svg"
            alt="Client Notification"
            style={{ width: '24px', height: '24px' }}
          />
        );
      default:
        return null;
    }
  };

  const renderTime = () => {
    const notificationTime = dayjs(message.time);
    const currentTime = dayjs();

    const diffMinutes = currentTime.diff(notificationTime, 'minutes');
    if (diffMinutes === 0) {
      return t('common.now');
    }
    if (diffMinutes < 60) {
      return `${diffMinutes} ${diffMinutes === 1 ? t('common.minute') : t('common.minutes')} ${t(
        'common.ago'
      )}`;
    }
    if (diffMinutes < 1440) {
      const diffHours = Math.floor(diffMinutes / 60);
      return `${diffHours} ${diffHours === 1 ? t('common.hour') : t('common.hours')} ${t(
        'common.ago'
      )}`;
    }
    return notificationTime.format('h:mm A');
  };

  const borderBottomStyle = index < totalItems - 1 ? '0.5px solid #FFDDC8' : 'none';

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      py="12px"
      borderBottom={borderBottomStyle}
      gap="7px"
      dir={appLanguage === 'ar' ? 'rtl' : 'ltr'}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <Box display="flex" gap="10px">
        <Box>{renderIcon()}</Box>
        <Box display="flex" flexDirection="column" gap="4px">
          <Typography variant="bodyBold" component="span">
            {message?.title}
          </Typography>
          <Typography variant="bodyRegular" fontSize="16px" lineHeight="19.2px">
            {message?.message}
          </Typography>
          <Typography variant="meta" color="textSecondary">
            {renderTime()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default NotificationMessageCard;
